import React, { useRef, useState } from "react";
import CircularLoader from "../CircularLoader";
import TableHeaders from "./table-header";

interface TableProps {
  headers: any[];
  emptyText?: string;
  children?: React.ReactNode;
  headerClassName?: string;
  tableClassName?: string;
  handleSort?: (columnId: string) => void;
  sortByOrder?: string;
  sortByColumn?: string;
  loading?: boolean;
  className?: string;
  isShowActions?: boolean;
  actionsHeadClassName?: string;
}

const StickyTable: React.FC<TableProps> = ({
  headers,
  emptyText = "There are no any boards",
  children = null,
  headerClassName,
  tableClassName,
  handleSort = () => {},
  sortByOrder,
  sortByColumn,
  loading,
  className,
  isShowActions,
  actionsHeadClassName,
}) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const resizingRef = useRef<boolean>(false);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    headers.reduce((acc, header) => {
      acc[header.id] = header.width ? Number(header.width) : 200;
      return acc;
    }, {} as Record<string, number>)
  );

  const handleColumnResize = (e: React.MouseEvent, columnId: string) => {
    if (!headers.find((header) => header.id === columnId)?.resizable) return;

    e.preventDefault();
    const table = tableRef.current;
    if (!table) return;

    const th = (e.target as HTMLElement).closest("th");
    if (!th) return;

    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    resizingRef.current = true;

    const tableRect = table.getBoundingClientRect();
    const minWidth = 50;
    const maxWidth = tableRect.width - (headers.length - 1) * minWidth;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return;

      const diff = moveEvent.clientX - startX;
      const newWidth = Math.min(
        Math.max(startWidth + diff, minWidth),
        maxWidth
      );

      setColumnWidths((prev) => ({
        ...prev,
        [columnId]: newWidth,
      }));

      th.style.width = `${newWidth}px`;
      th.style.minWidth = `${newWidth}px`;
    };

    const handleMouseUp = () => {
      resizingRef.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "default";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
  };

  return (
    <div className={`relative ${className}`}>
      <table ref={tableRef} className={`w-full text-sm ${tableClassName}`}>
        <thead className={`bg-gray-50 sticky z-20 group  ${headerClassName}`}>
          <tr>
            {isShowActions && (
              <th
                className={` h-[30px] text-left sticky left-0 z-40 bg-[#F4F5F7] ${actionsHeadClassName} `}
              ></th>
            )}
            {headers.map((header) => (
              <TableHeaders
                key={header.id}
                header={header}
                handleSort={handleSort}
                columnWidths={columnWidths}
                sortByColumn={sortByColumn}
                sortByOrder={sortByOrder ?? ""}
                handleColumnResize={handleColumnResize}
                isStickyFirstColumn={header?.id === "ticketName"}
                stickyLeft={isShowActions ? 62 : 0}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={headers.length + (isShowActions ? 1 : 0)}
                className="text-center py-8"
              >
                <div className="flex justify-center items-center w-full">
                  <CircularLoader size="md" className=" border-l-[#F4F5F7] " />
                </div>
              </td>
            </tr>
          ) : !children ? (
            <tr className=" ">
              {isShowActions && <td  className="sticky left-0 z-20  bg-[#F4F5F7] " />}
              <td
                colSpan={headers.length + (isShowActions ? 1 : 0)}
                className="text-sm leading-[21px] font-normal text-gray-500 text-center h-[52px] bg-white ms-[62px] "
              >
                {emptyText}
              </td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
};

export default StickyTable;
